import { baseApi } from "@/shared/api/baseApi";
import {
  authResponseSchema,
  invitationRegisterResponseSchema,
  logoutResponseSchema,
  meResponseSchema,
  passwordResetResponseSchema,
  refreshResponseSchema,
  sendCodeResponseSchema,
} from "@/features/auth/authApiSchemas";
import { pendingInvitationVault } from "@/features/team/pendingInvitationVault";

export type VerificationPurpose = "login" | "register" | "reset_password";

interface PhonePayload {
  phoneNumber: string;
}

interface SendCodePayload extends PhonePayload {
  purpose: VerificationPurpose;
}

interface PasswordLoginPayload extends PhonePayload {
  deviceDescription: string;
  password: string;
}

interface CodeLoginPayload extends PhonePayload {
  code: string;
  deviceDescription: string;
}

interface RegisterPayload extends CodeLoginPayload {
  displayName: string;
  organizationName: string;
  password: string;
}

interface InvitationRegisterPayload extends CodeLoginPayload {
  displayName: string;
  password: string;
}

interface ResetPasswordPayload extends PhonePayload {
  code: string;
  newPassword: string;
}

export const authApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    codeLogin: builder.mutation<
      ReturnType<typeof authResponseSchema.parse>,
      CodeLoginPayload
    >({
      query: ({ code, deviceDescription, phoneNumber }) => ({
        body: {
          code,
          device_description: deviceDescription,
          phone_number: phoneNumber,
        },
        method: "POST",
        url: "/v1/auth/phone/code-login",
      }),
      transformResponse: (response) => authResponseSchema.parse(response),
    }),
    getMe: builder.query<ReturnType<typeof meResponseSchema.parse>, void>({
      query: () => "/v1/me",
      transformResponse: (response) => meResponseSchema.parse(response),
    }),
    invitationRegister: builder.mutation<
      ReturnType<typeof invitationRegisterResponseSchema.parse>,
      InvitationRegisterPayload
    >({
      query: ({
        code,
        deviceDescription,
        displayName,
        password,
        phoneNumber,
      }) => ({
        body: {
          code,
          device_description: deviceDescription,
          display_name: displayName,
          password,
          phone_number: phoneNumber,
          token: pendingInvitationVault.get() ?? "",
        },
        method: "POST",
        url: "/v1/auth/phone/invitation-register",
      }),
      transformResponse: (response) =>
        invitationRegisterResponseSchema.parse(response),
    }),
    logout: builder.mutation<
      ReturnType<typeof logoutResponseSchema.parse>,
      void
    >({
      query: () => ({ method: "POST", url: "/v1/auth/logout" }),
      transformResponse: (response) => logoutResponseSchema.parse(response),
    }),
    passwordLogin: builder.mutation<
      ReturnType<typeof authResponseSchema.parse>,
      PasswordLoginPayload
    >({
      query: ({ deviceDescription, password, phoneNumber }) => ({
        body: {
          device_description: deviceDescription,
          password,
          phone_number: phoneNumber,
        },
        method: "POST",
        url: "/v1/auth/login",
      }),
      transformResponse: (response) => authResponseSchema.parse(response),
    }),
    refreshSession: builder.mutation<
      ReturnType<typeof refreshResponseSchema.parse> | null,
      void
    >({
      query: () => ({ method: "POST", url: "/v1/auth/refresh" }),
      transformResponse: (response) =>
        response == null ? null : refreshResponseSchema.parse(response),
    }),
    register: builder.mutation<
      ReturnType<typeof authResponseSchema.parse>,
      RegisterPayload
    >({
      query: ({
        code,
        deviceDescription,
        displayName,
        organizationName,
        password,
        phoneNumber,
      }) => ({
        body: {
          code,
          device_description: deviceDescription,
          display_name: displayName,
          organization_name: organizationName,
          password,
          phone_number: phoneNumber,
        },
        method: "POST",
        url: "/v1/auth/phone/register",
      }),
      transformResponse: (response) => authResponseSchema.parse(response),
    }),
    resetPassword: builder.mutation<
      ReturnType<typeof passwordResetResponseSchema.parse>,
      ResetPasswordPayload
    >({
      query: ({ code, newPassword, phoneNumber }) => ({
        body: {
          code,
          new_password: newPassword,
          phone_number: phoneNumber,
        },
        method: "POST",
        url: "/v1/auth/password/reset",
      }),
      transformResponse: (response) => passwordResetResponseSchema.parse(response),
    }),
    sendPhoneCode: builder.mutation<
      ReturnType<typeof sendCodeResponseSchema.parse>,
      SendCodePayload
    >({
      query: ({ phoneNumber, purpose }) => ({
        body: { phone_number: phoneNumber, purpose },
        method: "POST",
        url: "/v1/auth/phone/send-code",
      }),
      transformResponse: (response) => sendCodeResponseSchema.parse(response),
    }),
  }),
});

export const {
  useCodeLoginMutation,
  useInvitationRegisterMutation,
  useLogoutMutation,
  usePasswordLoginMutation,
  useRegisterMutation,
  useResetPasswordMutation,
  useSendPhoneCodeMutation,
} = authApi;
